import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MessageCircle, Loader2, BellRing } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { dateBR, dateTimeBR } from "../lib/format";
import { Card, CardHead, Pill } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { inviteLink } from "./AdmissionCard";

export const STAGE = {
  nao_aberto: ["Ainda não abriu o link", "bg-rose-50 text-rose-700 border-rose-200"],
  aberto: ["Abriu, mas não começou", "bg-amber-50 text-amber-800 border-amber-300"],
  em_preenchimento: ["Em preenchimento", "bg-sky-50 text-sky-700 border-sky-200"],
  enviado: ["Dados enviados", "bg-emerald-50 text-emerald-700 border-emerald-200"],
};

const waUrl = (item) => {
  const first = item.name.split(" ")[0];
  const msg = `Olá, ${first}! Passando para lembrar que sua admissão está aguardando seus dados. Leva poucos minutos pelo celular: ${inviteLink(item.token)}`;
  let ph = (item.phone || "").replace(/\D/g, "");
  if (ph.length === 10 || ph.length === 11) ph = `55${ph}`;
  return `https://wa.me/${ph}?text=${encodeURIComponent(msg)}`;
};

export function RemindActions({ item, onDone, size = "sm", emailLabel = "Lembrar por e-mail", only }) {
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(false);
  const [email, setEmail] = useState(item.email || "");
  const send = async (channel, mail) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admissions/by-employee/${item.employee_id}/remind`, { channel, ...(mail ? { email: mail } : {}) });
      toast.success(channel === "email" ? (data.queued ? `Na fila de envio para ${data.email}. O e-mail sai em instantes.` : `E-mail enviado para ${data.email}`) : "Envio por WhatsApp registrado");
      setAsk(false); onDone?.(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const id = item.employee_id;
  return (
    <div className="flex flex-wrap gap-2">
      {only !== "whatsapp" && <Button size={size} variant={only ? "default" : "outline"} data-testid={`remind-email-${id}`} disabled={busy} onClick={() => (item.email ? send("email") : setAsk(true))}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />}{emailLabel}
      </Button>}
      {only !== "email" && <Button size={size} variant={only ? "default" : "outline"} data-testid={`remind-whatsapp-${id}`} asChild>
        <a href={waUrl(item)} target="_blank" rel="noreferrer" onClick={() => send("whatsapp")}><MessageCircle className="h-3.5 w-3.5 mr-1.5" />{only ? "Abrir WhatsApp e enviar" : "WhatsApp"}</a>
      </Button>}
      <Dialog open={ask} onOpenChange={setAsk}>
        <DialogContent data-testid="remind-email-dialog">
          <DialogHeader><DialogTitle>E-mail do candidato</DialogTitle><DialogDescription>Ainda não temos o e-mail de {item.name}. Ele ficará salvo no cadastro.</DialogDescription></DialogHeader>
          <div><Label>E-mail</Label><Input data-testid="remind-email-input" type="email" className="mt-1.5" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.com" /></div>
          <DialogFooter><Button data-testid="remind-email-send" disabled={busy || !email.trim()} onClick={() => send("email", email.trim())}>{busy && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Enviar lembrete</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TrackerRow({ r, canRemind, onChanged }) {
  const [label, cls] = STAGE[r.stage];
  return (
    <div data-testid={`tracker-row-${r.employee_id}`} className="flex flex-col lg:flex-row lg:items-center gap-3 px-5 py-3.5">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/empresa/funcionarios/${r.employee_id}`} className="font-medium text-slate-900 hover:underline">{r.name}</Link>
          <Pill cls={cls} testid={`tracker-stage-${r.employee_id}`}>{label}</Pill>
          {r.expired && <Pill cls="bg-slate-100 text-slate-600 border-slate-200">Link expirado</Pill>}
        </div>
        <div className="text-xs text-slate-500 mt-1">
          {r.position || "—"} · link criado em {dateBR(r.created_at)}
          {r.opened_at ? ` · abriu em ${dateTimeBR(r.last_opened_at || r.opened_at)}` : ""}
          {r.reminders.length ? ` · ${r.reminders.length} lembrete(s), último em ${dateTimeBR(r.last_reminder_at)}` : " · nenhum lembrete enviado"}
          {r.stage === "em_preenchimento" ? ` · ${r.progress}% concluído` : ""}
        </div>
      </div>
      {canRemind && <RemindActions item={r} onDone={onChanged} />}
    </div>
  );
}

export function AdmissionTracker({ companyId }) {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admission-tracking", companyId ? { params: { company_id: companyId } } : {}).then((r) => setRows(r.data)).catch(() => setRows([])), [companyId]);
  useEffect(() => { load(); }, [load]);
  if (!rows?.length) return null;
  const notOpened = rows.filter((r) => r.stage === "nao_aberto").length;
  return (
    <Card testid="admission-tracker" className="mb-6">
      <CardHead title={<span className="inline-flex items-center gap-2"><BellRing className="h-4 w-4 text-sky-600" />Admissões digitais em andamento</span>}
        hint={<span data-testid="tracker-not-opened-count">{notOpened ? `${notOpened} candidato(s) ainda não abriram o link` : "Todos os candidatos já abriram o link"}</span>} />
      <div className="divide-y border-t">{rows.map((r) => <TrackerRow key={r.employee_id} r={r} canRemind={can("requests.create")} onChanged={load} />)}</div>
    </Card>
  );
}
